import {ArrowLeftOutlined} from "@ant-design/icons";
import {Alert, Button, Result} from "antd";
import {Link, useParams} from "react-router-dom";
import {
  StyledProductDetailsWrapper,
  StyledProductDetailsPageContainer,
  StyledProductDetailsMainSectionContainer,
} from "../../../../../components/products/product-details/product-details.style";
import {Footer} from "../../../../../components/public-profile/footer";
import {CustomerProductDetails} from "../../../../../components/products/customer-dashboard/product-details";
import {
  useCustomerPurchaseReferenceByProductId,
  useGetProductCustomer,
} from "../../../../../react-query-hooks/products";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {ErrorBoundary} from "react-error-boundary";

/**
 * Customer-facing detail for one purchased digital product.
 *
 * The route param (`/customer/products/details/:id`) predates v2 and carries a
 * numeric *product* id, because v1's `customer/products/:id` was keyed that way.
 * v2 keys the purchase detail by the purchase `reference` instead, so a numeric
 * param is first resolved to a reference via the customer's library; a
 * non-numeric param is taken to already be a reference, which is what any link
 * minted from now on should use.
 */
export function CustomerProductDetailsPage() {
  const {id} = useParams();

  // References are non-numeric (e.g. "PRD-TXN-..."), product ids are all digits.
  const legacyProductId = id && /^\d+$/.test(id) ? Number(id) : undefined;
  const referenceLookup = useCustomerPurchaseReferenceByProductId(legacyProductId);
  const reference = legacyProductId ? referenceLookup.data ?? undefined : id;

  const getProductCustomer = useGetProductCustomer(reference);

  const notFound = (
    <Result
      status="404"
      title="Product not found"
      subTitle="We couldn't find this product in your library."
      extra={
        <Button size="small" type="default">
          <Link to="/customer/products">Go back</Link>
        </Button>
      }
    />
  );

  if (legacyProductId && referenceLookup.isLoading) {
    return <FullPageLoader />;
  }
  if (legacyProductId && referenceLookup.isError) {
    return (
      <Result
        status="error"
        title="An Error Occurred"
        subTitle={referenceLookup.error.message}
        extra={[
          <Button
            onClick={() => referenceLookup.refetch()}
            type="primary"
            key="retry"
            size="small"
          >
            Try again
          </Button>,
          <Button size="small" type="default" key="back">
            <Link to="/customer/products">Go back</Link>
          </Button>,
        ]}
      />
    );
  }
  if (!reference) {
    return notFound;
  }
  if (getProductCustomer.isLoading) {
    return <FullPageLoader />;
  }
  if (getProductCustomer.isError) {
    return (
      <Result
        status="error"
        title="An Error Occurred"
        subTitle={getProductCustomer.error.message}
        extra={[
          <Button
            onClick={() => getProductCustomer.refetch()}
            type="primary"
            key="console"
            size="small"
          >
            Try again
          </Button>,
          <Button size="small" type="default" key="console">
            <Link to="/customer/products">Go back</Link>
          </Button>,
        ]}
      />
    );
  }
  return (
    <div style={{position: "relative", minHeight: "100vh"}}>
      <StyledProductDetailsWrapper>
        <StyledProductDetailsPageContainer>
          <Link to="/customer/products">
            <Button icon={<ArrowLeftOutlined />} type="link" size="small">
              Back
            </Button>
          </Link>
          <StyledProductDetailsMainSectionContainer>
            <ErrorBoundary
              FallbackComponent={({error, resetErrorBoundary}) => (
                <div>
                  <Alert
                    message="Sorry, something went wrong."
                    showIcon
                    description={error.message}
                    type="error"
                    action={
                      <Button
                        onClick={() => {
                          resetErrorBoundary();
                        }}
                        size="small"
                        danger
                      >
                        Reset
                      </Button>
                    }
                  />
                </div>
              )}
            >
              <CustomerProductDetails productData={getProductCustomer.data} />
            </ErrorBoundary>
          </StyledProductDetailsMainSectionContainer>
        </StyledProductDetailsPageContainer>
      </StyledProductDetailsWrapper>
      <Footer />
    </div>
  );
}
